import { BenefitingPerson } from 'api';
import React, { FunctionComponent, useState } from 'react';
import Translate from 'ui/atoms/translate';
import { Modal, makeModalForm, ModalHeader, ModalContent, ModalFooter, ModalFooterButtons } from 'ui/molecules/modal';
import * as Styled from './styled';
import BenefitingPersonDataForm from './benefiting-person-form';
import Section from 'ui/atoms/section';
import Accordion from 'ui/atoms/accordion';
import Button from 'ui/atoms/button';
import Hint from 'ui/atoms/hint';
import useEditBenefitingPerson from './hooks/use-edit-benefiting-person';
import Icon from 'src/ui/atoms/icon';

export interface AddManualReviewModalProps {
  onHide: () => void;
  investorId: string;
  benefitingOwners?: BenefitingPerson[];
  fetchInvestorDetails?: () => void;
  completeManualReview?: boolean;
}

export interface BenefitingPersonsOverviewFields {
  acceptPep: boolean;
}

const BenefitingPersonAcceptPepForm = makeModalForm<BenefitingPersonsOverviewFields>();

const BenefitingPersonsModal: FunctionComponent<AddManualReviewModalProps> = (props) => {
  const { onHide, investorId, benefitingOwners = [], fetchInvestorDetails, completeManualReview } = props;

  const [acceptPep, setAcceptPep] = useState(false);

  const {
    benefitingPersons,
    expandBenefitingPerson,
    deletePerson,
    isPersonExpanded,
    addPerson,
    onPersonChange,
    getPersonByIndex,
    isBenefitingPersonsEmpty,
    onDataSubmit,
    error,
  } = useEditBenefitingPerson(
    investorId,
    benefitingOwners,
    onHide,
    fetchInvestorDetails,
    setAcceptPep,
    completeManualReview,
  );

  const getDisplayedContentOfPerson = (index: number, forename: string, surname: string, isExpanded: boolean) => {
    const name = `${forename || ''} ${surname || ''}`;
    return (
      <Styled.PersonRowContainer>
        <div>
          {(forename || surname) && <Styled.PersonName>{name}</Styled.PersonName>}
          <Icon name={isExpanded ? 'angle-up' : 'angle-down'} color="link" />
        </div>
        <Button variant="link" icon="trash" color="primary" onClick={(event) => deletePerson(index, event)} />
      </Styled.PersonRowContainer>
    );
  };

  return (
    <Modal onClose={onHide}>
      <BenefitingPersonAcceptPepForm onSubmit={onDataSubmit} error={error} initial={{ acceptPep: acceptPep }}>
        <ModalHeader>
          <Translate
            name={
              completeManualReview ? 'kycComplianceDetails.addManualReview.title' : 'benefitingPersons.editOnSummary'
            }
          />
        </ModalHeader>
        <ModalContent>
          <Section spacing="regular">
            <Translate name="kycComplianceDetails.addManualReview.benefitingPersonsInfo" />
          </Section>
          {!isBenefitingPersonsEmpty() &&
            benefitingPersons.map((person, index) => {
              if (person.action === 'delete') return null;

              const isExpanded = isPersonExpanded(index);
              return (
                <div key={index}>
                  <Accordion
                    key={index}
                    labelComponent={getDisplayedContentOfPerson(index, person.forename, person.surname, isExpanded)}
                    expanded={isExpanded}
                    onChange={() => expandBenefitingPerson(index)}
                    showExpandIcon={false}
                  >
                    <BenefitingPersonDataForm benefitingPerson={getPersonByIndex(index)} onChange={onPersonChange} />
                  </Accordion>
                </div>
              );
            })}
          <Section spacing="small">
            <Styled.AddMoreButton variant="link" icon="plus" onClick={addPerson}>
              <Translate name="kycComplianceDetails.addManualReview.addPersonButton" />
            </Styled.AddMoreButton>
          </Section>
          <BenefitingPersonAcceptPepForm.Group
            name="acceptPep"
            required={true}
            spacing={isBenefitingPersonsEmpty() ? 'medium' : 'none'}
          >
            <BenefitingPersonAcceptPepForm.Checkbox checked={acceptPep}>
              <Translate name="kycComplianceDetails.addManualReview.benefitingPersonsCheckbox" />
            </BenefitingPersonAcceptPepForm.Checkbox>
          </BenefitingPersonAcceptPepForm.Group>
          {isBenefitingPersonsEmpty() && (
            <Hint variant="danger">
              <Translate name="kycComplianceDetails.addManualReview.noBenefitingPersonsError" />
            </Hint>
          )}
        </ModalContent>
        <ModalFooter>
          <ModalFooterButtons
            actionButtons={[
              {
                name: 'cancel',
                content: <Translate name="common.cancel" />,
                size: 'large',
                onClick: onHide,
              },
              {
                name: 'send',
                content: (
                  <Translate
                    name={
                      completeManualReview
                        ? 'kycComplianceDetails.addManualReview.confirmReviewButton'
                        : 'common.saveChanges'
                    }
                  />
                ),
                variant: 'primary',
                size: 'large',
                type: 'submit',
                disabled: isBenefitingPersonsEmpty(),
              },
            ]}
          />
        </ModalFooter>
      </BenefitingPersonAcceptPepForm>
    </Modal>
  );
};

export default BenefitingPersonsModal;
